import React, { createContext, useEffect, useState } from 'react'

import { GenericResponse, GenericStateType } from 'utils/types'

import { api } from '../../utils/api'
import { getError } from '../../utils/helpers/getError'
import { useCustomContext } from '../../utils/helpers/useCustomContext'

import {
  RegisterResponseType,
  RegisterStepContextType,
  RegisterStepOneType,
  RegisterStepStateType,
  RegisterStepThreeType,
  RegisterStepTwoType,
  RegisterType,
} from './registerStepTypes'

const registerResponse: RegisterResponseType = {
  message: '',
  success: true,
}

const INITIAL_REGISTER_STEP_CONTEXT: RegisterStepContextType = {
  setLoading: () => null,
  setError: () => null,
  clearError: () => null,
  clearContent: () => null,
  nextStep: () => null,
  prevStep: () => null,
  register: () => new Promise(() => registerResponse),
  stepOneDispatch: () => null,
  stepTwoDispatch: () => null,
  stepThreeDispatch: () => new Promise(() => ''),
  registerStepState: {
    error: '',
    loading: false,
    state: {
      totalScreens: 3,
      step: 0,
      registerMock: {
        email: '',
        confirmEmail: '',
        phone: '',
        firstName: '',
        lastName: '',
        rut: '',
        password: '',
        confirmPassword: '',
      },
    },
  },
}

const RegisterStepContext = createContext<RegisterStepContextType>(
  INITIAL_REGISTER_STEP_CONTEXT,
)

const RegisterStepProvider: React.FC = ({ children }) => {
  const [registerStepState, setRegisterStepState] = useState<
    GenericStateType<RegisterStepStateType>
  >(INITIAL_REGISTER_STEP_CONTEXT.registerStepState)

  const setLoading = (value = true) =>
    setRegisterStepState(prevState => ({ ...prevState, loading: value })) // Option 2

  const setError = (msg: string) =>
    setRegisterStepState(prevState => ({ ...prevState, loading: false, error: msg }))

  const clearError = () =>
    setRegisterStepState(prevState => ({ ...prevState, error: '' }))

  const nextStep = () => {
    setRegisterStepState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        step: ++registerStepState.state.step,
      },
    }))
  }

  const prevStep = () =>
    setRegisterStepState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        step: --registerStepState.state.step,
      },
    }))

  const register =
    async (data: RegisterType) => {
      try {
        const response = await api.post<GenericResponse<RegisterType>>(
          'Auth/CreateUser',
          data,
        )

        return {
          message: response.data.message,
          success: true,
        }
      } catch (err) {
        const msg = getError(err)

        // TODO: Este seteo de error provoca que al intentar crear la cuenta se muestren 2 veces el error
        // setError(msg)

        return {
          message: msg,
          success: false,
        }
      }
    }

  const handleSubmitRegister = async (password: string) => {
    const {
      state: {
        registerMock: { firstName, lastName, rut, phone, email },
      },
    } = registerStepState

    const data: RegisterType = {
      name: firstName,
      lastName,
      citizenId: rut.trim().toLowerCase(),
      phoneNumber: `+569${phone.trim()}`,
      email: email.trim().toLowerCase(),
      password: password.trim(),
    }

    const success = await register(data)

    return success
  }

  const validateEmailOrPhoneNumber = async (email: string, phone: string) => {
    try {
      setLoading()

      await api.get<GenericResponse<boolean>>(`User/${email}/${phone}/ValidateEmailOrPhone`)

      setLoading(false)

      return true
    } catch (err) {
      const msg = getError(err)

      setError(msg)

      return false
    }
  }

  const validateCitizenId = async (rut: string) => {
    try {
      setLoading()

      await api.get<GenericResponse<boolean>>(`User/${rut}/ValidateCitizenId`)

      setLoading(false)

      return true
    } catch (err) {
      const msg = getError(err)

      setError(msg)

      return false
    }
  }

  const stepOneDispatch = async ({ email, confirmEmail, phone }: RegisterStepOneType) => {
    const isValidate = await validateEmailOrPhoneNumber(email, `+569${phone.trim()}`)

    if (isValidate)
      setRegisterStepState(prevState => ({
        ...prevState,
        state: {
          ...prevState.state,
          step: 1,
          registerMock: { ...prevState.state.registerMock, email, confirmEmail, phone },
        },
      }))
  }

  const stepTwoDispatch = async ({ firstName, lastName, rut }: RegisterStepTwoType) => {
    const isValidate = await validateCitizenId(rut)

    if (isValidate)
      setRegisterStepState(prevState => ({
        ...prevState,
        state: {
          ...prevState.state,
          step: 2,
          registerMock: {
            ...prevState.state.registerMock,
            firstName,
            lastName,
            rut,
          },
        },
      }))

  }

  const clearContent = () =>
    setRegisterStepState(prevState => ({
      ...prevState,
      error: '',
      loading: false,
      state: {
        totalScreens: 3,
        step: 0,
        registerMock: {
          email: '',
          confirmEmail: '',
          phone: '',
          firstName: '',
          lastName: '',
          rut: '',
          password: '',
          confirmPassword: '',
        },
      },
    }))

  const stepThreeDispatch = async ({ password, confirmPassword }: RegisterStepThreeType) => {
    setLoading()
    registerStepState.error !== '' && clearError()
    setRegisterStepState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        registerMock: {
          ...prevState.state.registerMock,
          password,
          confirmPassword,
        },
      },
    }))

    const success = await handleSubmitRegister(password)

    setLoading(false)
    clearContent()

    return success
  }

  useEffect(() => {
    setRegisterStepState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        step: 0,
      },
    }))
  }, [])

  return (
    <RegisterStepContext.Provider
      value={{
        registerStepState,
        setLoading,
        setError,
        clearError,
        clearContent,
        nextStep,
        prevStep,
        register,
        stepOneDispatch,
        stepTwoDispatch,
        stepThreeDispatch,
      }}>
      {children}
    </RegisterStepContext.Provider>
  )
}

const useRegisterStep = () => useCustomContext(RegisterStepContext)

export { RegisterStepProvider, useRegisterStep }
